// Media grid + tiles + list table (WL-023). Pure/presentational — shared by the
// Media Library page AND the reuse picker so one grid system serves both.
import React from "react";
import Checkbox from "@mui/material/Checkbox";
import * as S from "./MediaLibrary.styled";
import { TYPE_GLYPH } from "./icons";
import { IconTarget, IconCheck, IconKebab, IconInfo, IconCapture, IconEdit } from "./icons";
import { formatBytes, formatDuration, TYPE_LABEL, refcountLabel, COLUMN_SORT_KEYS } from "../../pages/MediaLibrary/helpers";
import { useVideoMeta } from "./useVideoMeta";

// A double-click needs to win over the single-click "play" that fired on the
// first of its two clicks — this is the window a click waits before running,
// so a second click inside it cancels play and runs "expand" instead.
const DBLCLICK_WINDOW_MS = 220;

// Segment-hue gradient placeholder (deck thumbs). Deterministic per item.
const HUES = ["#34D399", "#6D5EF6", "#F472B6", "#F59E0B", "#22D3EE", "#A78BFA"];
export function thumbGradient(item, i = 0) {
  const base = HUES[(item?.hue ?? i) % HUES.length];
  const alt = HUES[((item?.hue ?? i) + 2) % HUES.length];
  return `linear-gradient(135deg, ${base} 0%, ${alt} 100%)`;
}

// QA-113 (Kelly, 2026-08-06): the renderable thumbnail source for a media item.
// Media rows carry `thumbnail` (image types, real-shape) and always a `url`; the
// tile previously ignored both and drew only a gradient + glyph. Returns null for
// items with no image to show (video/audio/document keep their type glyph).
export function getTileThumbSrc(item) {
  if (!item) return null;
  if (item.thumbnail) return item.thumbnail;
  if (item.type === "image" && item.url) return item.url;
  return null;
}

const Glyph = ({ type, size }) => {
  const g = TYPE_GLYPH[type] || TYPE_GLYPH.image;
  const { Comp, color } = g;
  return (
    <span style={{ color, display: "inline-flex" }}>
      <Comp size={size} />
    </span>
  );
};

// Short "Jun 18" date.
function shortDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

// `compact` (E2E-006 / E2E-006b, Gordon 2026-07-24): the reuse picker is a dense
// 4-col pane where a thumbnail + name suffices; the size·date meta line and the
// icon+text Capture badge are clutter that crowds/obscures the thumb. In compact
// mode we drop the meta line (size stays in the aria-label + the list view) and
// shrink the Capture badge to an icon-only corner chip (provenance kept via title).
// The full Media Library page passes compact=false → its rich display is untouched.
export function MediaTile({
  item, index, selected, small, compact = false, showHoverPill = true,
  onActivate, onPlay, onExpand, onToggleSelect, onRename, disabled = false, disabledReason,
}) {
  const isCapture = item.source === "capture";
  const [thumbFailed, setThumbFailed] = React.useState(false);
  const baseThumbSrc = getTileThumbSrc(item);
  const explicitDuration = formatDuration(item.duration);
  // #960: the live API gives video items neither a `thumbnail` nor a
  // `duration` (mediaAdapter.js) — only decode client-side what's missing.
  const needsVideoMeta = item.type === "video" && Boolean(item.url) && (!baseThumbSrc || !explicitDuration);
  const videoMeta = useVideoMeta(needsVideoMeta ? item.url : null, needsVideoMeta);
  const thumbSrc = thumbFailed ? null : (baseThumbSrc || (item.type === "video" ? videoMeta.poster : null));
  const dur = explicitDuration || (item.type === "video" ? formatDuration(videoMeta.duration) : null);

  // #960: single click plays, double click expands. When no onExpand is wired
  // (every OTHER consumer of this shared tile — the reuse picker, the move
  // modal, stock images — still just "activates"/selects on click), skip the
  // debounce entirely so their click stays perfectly synchronous, unchanged.
  const clickTimerRef = React.useRef(null);
  React.useEffect(() => () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
  }, []);
  const runPrimary = () => {
    const primary = onPlay || onActivate;
    if (!disabled && primary) primary(item);
  };
  const runExpand = () => {
    if (!disabled && onExpand) onExpand(item);
  };
  const handleClick = () => {
    if (disabled) return;
    if (!onExpand) {
      runPrimary();
      return;
    }
    if (clickTimerRef.current) return;
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      runPrimary();
    }, DBLCLICK_WINDOW_MS);
  };
  const handleDoubleClick = () => {
    if (disabled || !onExpand) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    runExpand();
  };

  const captureNote = isCapture
    ? ` From Capture${item.caption ? `: ${item.caption}` : ""}.`
    : "";
  const label = disabled
    ? `${item.name}, ${TYPE_LABEL[item.type] || "file"}.${captureNote} ${disabledReason || "Not available here"}`
    : `${item.name}, ${TYPE_LABEL[item.type] || "file"}, ${formatBytes(item.size)}.${captureNote} ${refcountLabel(item.usedInWheels)}`;
  return (
    <S.TileWrapper>
      <S.Tile
        selected={selected}
        disabled={disabled ? 1 : 0}
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        aria-disabled={disabled || undefined}
        aria-label={label}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            runPrimary();
          }
        }}
        data-testid="media-tile"
        data-disabled={disabled ? "true" : undefined}
        data-avoid-search-pill="true"
      >
      <S.Thumb gradient={thumbGradient(item, index)} small={small}>
        {thumbSrc && (
          <S.ThumbImg
            src={thumbSrc}
            alt=""
            loading="lazy"
            draggable={false}
            onError={() => setThumbFailed(true)}
            data-testid="media-thumb-img"
          />
        )}
        {/* capture-ship-medialib-lists-403-avatar-objects-as-blank-tiles: an
            "image" item whose thumbnail request 403s (e.g. a moderation-
            quarantined avatar upload, see users/views.py::get_quarantined_keys)
            sets thumbFailed and thumbSrc goes null just like a broken
            video/audio/document — it needs the same glyph fallback instead of
            an empty gradient tile. */}
        {!thumbSrc && ["video", "audio", "document", "image"].includes(item.type) && (
          <span data-testid="media-thumb-glyph">
            <Glyph type={item.type} size={small ? 20 : 34} />
          </span>
        )}
        {dur && item.type === "video" && <S.DurationChip>{dur}</S.DurationChip>}
        {selected && !disabled && !onToggleSelect && (
          <S.CheckChip aria-hidden="true">
            <IconCheck size={15} />
          </S.CheckChip>
        )}
        {showHoverPill && !selected && !disabled && (
          <S.HoverPill className="hoverpill">
            <span className="pill">
              <IconTarget size={15} /> Use in a Wheel
            </span>
          </S.HoverPill>
        )}
        {isCapture && (
          <S.CaptureBadge
            data-testid="capture-badge"
            title="Saved from Capture"
            compact={compact ? 1 : 0}
          >
            <IconCapture size={12} />
            {!compact && <> Capture</>}
          </S.CaptureBadge>
        )}
      </S.Thumb>
      <S.TileCaption>
        <div className="fn" title={item.name}>
          <span
            className="type-ico"
            aria-hidden="true"
            style={{ display: "inline-flex", verticalAlign: "middle", marginRight: 4 }}
            data-testid="media-tile-type-icon"
          >
            <Glyph type={item.type} size={13} />
          </span>
          {item.name}
        </div>
        {disabled ? (
          <S.IneligibleNote data-testid="ineligible-note">
            <IconInfo size={13} /> {disabledReason || "Not available here"}
          </S.IneligibleNote>
        ) : compact ? null : (
          <div className="meta">
            {formatBytes(item.size)}
            {item.uploadedAt ? ` · ${shortDate(item.uploadedAt)}` : ""}
          </div>
        )}
        {isCapture && item.caption && (
          <div className="cap" title={item.caption} data-testid="capture-caption">
            {item.caption}
          </div>
        )}
      </S.TileCaption>
      </S.Tile>
      {onToggleSelect && (
        <S.SelectCheckbox>
          {/* gordon-0831-media-checkboxes-off-design: a bare native
              <input type="checkbox"> renders with the browser/OS's own
              (unbranded) unchecked-state chrome — every other multi-select
              surface in the app (Storage table, Content Moderation table)
              uses MUI's SVG-drawn Checkbox instead, so this one now matches. */}
          <Checkbox
            checked={Boolean(selected)}
            disabled={disabled}
            onChange={() => onToggleSelect(item)}
            size="small"
            sx={{
              padding: 0,
              color: S.C.purpleDeep,
              "&.Mui-checked": { color: S.C.purpleDeep },
            }}
            inputProps={{
              "aria-label": `Select ${item.name}`,
              "data-testid": "media-tile-checkbox",
            }}
          />
        </S.SelectCheckbox>
      )}
      {onRename && !disabled && (
        <S.RenameChip
          type="button"
          aria-label={`Rename ${item.name}`}
          title="Rename"
          onClick={() => onRename(item)}
        >
          <IconEdit size={14} />
        </S.RenameChip>
      )}
    </S.TileWrapper>
  );
}

export function MediaGrid({
  items, cols = 5, small = false, compact = false, selectedIds = [], showHoverPill,
  onActivate, onPlay, onExpand, onToggleSelect, onRename, isDisabled, disabledReason,
}) {
  return (
    <S.Grid cols={cols} data-testid="media-grid">
      {items.map((item, i) => {
        const disabled = isDisabled ? !!isDisabled(item) : false;
        return (
          <MediaTile
            key={item.id ?? i}
            item={item}
            index={i}
            small={small}
            compact={compact}
            selected={selectedIds.includes(item.id)}
            showHoverPill={showHoverPill}
            onActivate={onActivate}
            onPlay={onPlay}
            onExpand={onExpand}
            onToggleSelect={onToggleSelect}
            onRename={onRename}
            disabled={disabled}
            disabledReason={disabled && disabledReason ? disabledReason(item) : undefined}
          />
        );
      })}
    </S.Grid>
  );
}

// A list-view column header. Plain inert <span> when the caller doesn't wire
// sorting (the reuse-picker's MediaListTable never passes onSort — its headers
// stay exactly as before). When onSort IS passed, becomes a real
// role="columnheader" control: click or Enter/Space fires onSort(columnKey),
// aria-sort reflects whether this column is the active sort.
function ColumnHeader({ columnKey, label, className, sortKey, onSort }) {
  if (!onSort) {
    return <span className={className}>{label}</span>;
  }
  const active = COLUMN_SORT_KEYS[columnKey] === sortKey;
  return (
    <span
      className={className}
      role="columnheader"
      tabIndex={0}
      aria-sort={active ? "ascending" : "none"}
      style={{ cursor: "pointer" }}
      onClick={() => onSort(columnKey)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSort(columnKey);
        }
      }}
    >
      {label}
    </span>
  );
}

// List/detail view — fixed columns, every edge lines up by grid template.
export function MediaListTable({ items, selectedIds = [], onActivate, onRename, showKebab = true, isDisabled, disabledReason, sortKey, onSort }) {
  return (
    <S.ListTable data-testid="media-list">
      <S.ListHeader role="row">
        <span aria-hidden="true" />
        <ColumnHeader columnKey="name" label="Name" sortKey={sortKey} onSort={onSort} />
        <ColumnHeader columnKey="type" label="Type" sortKey={sortKey} onSort={onSort} />
        <ColumnHeader columnKey="size" label="Size" className="num" sortKey={sortKey} onSort={onSort} />
        <ColumnHeader columnKey="uploaded" label="Uploaded" className="num" sortKey={sortKey} onSort={onSort} />
        <span>Folder</span>
        <span aria-hidden="true" />
      </S.ListHeader>
      {items.map((item, i) => {
        const selected = selectedIds.includes(item.id);
        const disabled = isDisabled ? !!isDisabled(item) : false;
        const reason = disabled && disabledReason ? disabledReason(item) : undefined;
        const activate = () => !disabled && onActivate && onActivate(item);
        return (
          <S.ListRow
            key={item.id ?? i}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-disabled={disabled || undefined}
            aria-label={
              disabled
                ? `${item.name}, ${TYPE_LABEL[item.type] || "file"}. ${reason || "Not available here"}`
                : `${item.name}, ${TYPE_LABEL[item.type] || "file"}`
            }
            title={disabled ? reason : undefined}
            onClick={activate}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate();
              }
            }}
            style={
              disabled
                ? { opacity: 0.55, cursor: "not-allowed" }
                : selected
                ? { background: "#FBF9FF", boxShadow: "inset 3px 0 0 #8F38EF" }
                : undefined
            }
            data-testid="media-list-row"
            data-disabled={disabled ? "true" : undefined}
          >
            <S.ListThumb gradient={thumbGradient(item, i)} data-testid="media-list-thumb">
              {getTileThumbSrc(item) ? (
                <S.ThumbImg
                  src={getTileThumbSrc(item)}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  data-testid="media-list-thumb-img"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <Glyph type={item.type} size={14} />
              )}
            </S.ListThumb>
            <span className="name" title={item.name}>{item.name}</span>
            <span className="cell">
              {disabled ? (
                <S.IneligibleNote data-testid="ineligible-note">
                  <IconInfo size={13} /> {reason || "Not available here"}
                </S.IneligibleNote>
              ) : (
                <>
                  <Glyph type={item.type} size={16} /> {TYPE_LABEL[item.type] || "File"}
                  {item.source === "capture" && (
                    <span
                      data-testid="capture-badge"
                      title={item.caption ? `From Capture: ${item.caption}` : "Saved from Capture"}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        marginLeft: 6,
                        color: "#6F27D6",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      <IconCapture size={12} /> Capture
                    </span>
                  )}
                </>
              )}
            </span>
            <span className="num">{formatBytes(item.size)}</span>
            <span className="num">{shortDate(item.uploadedAt)}</span>
            <span className="cell">
              {item.folder ? (
                <>
                  <span style={{ color: "#9AA0A6", display: "inline-flex" }}>
                    <TYPE_GLYPH.image.Comp size={0} />
                  </span>
                  <FolderGlyph /> {item.folder}
                </>
              ) : (
                <span className="muted">—</span>
              )}
            </span>
            {onRename && !disabled ? (
              <button
                type="button"
                aria-label={`Rename ${item.name}`}
                title="Rename"
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(item);
                }}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "#565556", justifySelf: "center" }}
              >
                <IconEdit size={18} />
              </button>
            ) : showKebab ? (
              <button
                type="button"
                aria-label={`More actions for ${item.name}`}
                onClick={(e) => e.stopPropagation()}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "#565556", justifySelf: "center" }}
              >
                <IconKebab size={18} />
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
          </S.ListRow>
        );
      })}
    </S.ListTable>
  );
}

function FolderGlyph() {
  return (
    <span style={{ color: "#9AA0A6", display: "inline-flex" }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      </svg>
    </span>
  );
}
